// Transparent client-side proximity score between a listing and a contact's search
// profile. Deliberately simple and inspectable — this is NOT the server-side
// listing→buyers confidence model (listingsMatch.service.ts); it is a small UI-side
// closeness indicator for the "הוספת נכסים" discovery grid.
//
// Honesty rules (explicit product directives):
// - Score only on criteria the profile actually has (budget / rooms / size / location / type).
// - Require at least MIN_AVAILABLE_COMPONENTS signals — otherwise return null and the
//   UI hides the badge instead of faking precision.
// - Round to the nearest 5.
import { rooms as roomValues } from "../../../../../../@types/sharedSchema";
import type { ProcessedListingData } from "../../../../../../@types/sharedSchema";
import type { MatchFilters } from "./contactMatchFilters";

const WEIGHTS = {
  budget: 35,
  location: 30,
  rooms: 25,
  // Built area weighs what rooms does — the same decision as the server's listing→buyers score
  // (`MATCH_SCORE_WEIGHTS`): they are the two ways a customer says how big.
  size: 25,
  propertyType: 10
} as const;

const MIN_AVAILABLE_COMPONENTS = 2;

type ScoreComponent = { weight: number; score: number };

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

function budgetComponent(
  listing: ProcessedListingData,
  profile: MatchFilters
): ScoreComponent | null {
  const { minPrice, maxPrice } = profile;
  if (!minPrice && !maxPrice) return null;
  const price = listing.price;
  if (typeof price !== "number" || !isFinite(price) || price <= 0) return null;
  let score = 1;
  if (maxPrice && price > maxPrice) {
    // 2.5x decay: 10% over budget => 0.75, 40% over => 0.
    score = clamp01(1 - ((price - maxPrice) / maxPrice) * 2.5);
  } else if (minPrice && price < minPrice) {
    score = clamp01(1 - ((minPrice - price) / minPrice) * 2.5);
  }
  return { weight: WEIGHTS.budget, score };
}

function roomsComponent(
  listing: ProcessedListingData,
  profile: MatchFilters
): ScoreComponent | null {
  const { minRooms } = profile;
  // The filter builder treats maxRooms === top-of-scale (6) as "6+" (unbounded above,
  // ported legacy quirk) — mirror that here so listings the query deliberately
  // includes aren't penalized by the badge for exceeding a bound the search ignores.
  const maxRooms =
    profile.maxRooms !== undefined && profile.maxRooms >= roomValues[roomValues.length - 1]
      ? undefined
      : profile.maxRooms;
  if (minRooms === undefined && maxRooms === undefined) return null;
  const rooms = listing.roomsCount;
  if (typeof rooms !== "number" || !isFinite(rooms)) return null;
  let distance = 0;
  if (minRooms !== undefined && rooms < minRooms) distance = minRooms - rooms;
  else if (maxRooms !== undefined && rooms > maxRooms) distance = rooms - maxRooms;
  // Each missing room costs 35% of the component.
  return { weight: WEIGHTS.rooms, score: clamp01(1 - distance * 0.35) };
}

/**
 * The same distances the server's `evaluateSize` draws its lines at (listingMatchRules.ts),
 * so a listing reads here the way its buyer reads on the listing's own matches tab:
 * up to 5% under the minimum is two rounded numbers; 20% under is where the server stops calling
 * the pair a match at all; up to 20% over the maximum is simply a match.
 */
const SIZE_ROUNDING_TOLERANCE = 0.05;
const SIZE_SHORT_ZERO_AT = 0.2;
const SIZE_OVER_TOLERANCE = 0.2;
/** What the server scores a tolerated miss at; "bigger than asked" never sinks below it. */
const SIZE_OVER_FLOOR = 0.3;
/** Under this many m² per room the listing's size is a placeholder ("4 rooms, 1 m²"), not a size. */
const MIN_PLAUSIBLE_SQM_PER_ROOM = 10;

const positive = (value: unknown): number | undefined =>
  typeof value === "number" && isFinite(value) && value > 0 ? value : undefined;

function sizeComponent(
  listing: ProcessedListingData,
  profile: MatchFilters
): ScoreComponent | null {
  const min = positive(profile.minSquareMeter);
  const max = positive(profile.maxSquareMeter);
  if (min === undefined && max === undefined) return null;
  const size = positive(listing.squareMeter);
  // The listing does not say (or states a placeholder): not scored — neither a hit nor a miss.
  if (size === undefined) return null;
  const rooms = positive(listing.roomsCount);
  if (rooms !== undefined && rooms >= 2 && size < rooms * MIN_PLAUSIBLE_SQM_PER_ROOM) return null;

  let score = 1;
  if (min !== undefined && size < min) {
    const shortBy = (min - size) / min;
    score = clamp01(
      1 - (shortBy - SIZE_ROUNDING_TOLERANCE) / (SIZE_SHORT_ZERO_AT - SIZE_ROUNDING_TOLERANCE)
    );
  } else if (max !== undefined && size > max * (1 + SIZE_OVER_TOLERANCE)) {
    // Larger than asked is worth seeing — its cost is the budget component's problem.
    score = SIZE_OVER_FLOOR;
  }
  return { weight: WEIGHTS.size, score };
}

const norm = (s: string | undefined | null): string => (s ?? "").trim();

function locationComponent(
  listing: ProcessedListingData,
  profile: MatchFilters
): ScoreComponent | null {
  const locations = profile.locations ?? [];
  if (locations.length === 0) return null;
  const city = norm(listing.address?.city);
  const neighborhood = norm(listing.address?.neighborhood);
  const street = norm(listing.address?.street);

  let best = 0;
  for (const loc of locations) {
    const locCity = norm(loc.city);
    const locNeighborhood = norm(loc.neighborhood);
    const locStreet = norm(loc.street);
    const cityOk = !locCity || (!!city && city === locCity);
    if (!cityOk) continue;
    if (locStreet) {
      best = Math.max(best, street === locStreet ? 1 : locCity && city === locCity ? 0.6 : 0);
    } else if (locNeighborhood) {
      best = Math.max(
        best,
        neighborhood === locNeighborhood ? 1 : locCity && city === locCity ? 0.6 : 0
      );
    } else if (locCity) {
      // City-only interest: being in the city is a full match for that interest.
      best = Math.max(best, city === locCity ? 1 : 0);
    }
    if (best === 1) break;
  }
  return { weight: WEIGHTS.location, score: best };
}

function propertyTypeComponent(
  listing: ProcessedListingData,
  profile: MatchFilters
): ScoreComponent | null {
  const types = profile.propertyTypes ?? [];
  if (types.length === 0) return null;
  if (!listing.propertyType) return null;
  return { weight: WEIGHTS.propertyType, score: types.includes(listing.propertyType) ? 1 : 0 };
}

/**
 * Weighted-average closeness in percent, rounded to 5s — or null when the profile
 * doesn't carry enough signal to score honestly.
 */
export function computeMatchScore(
  listing: ProcessedListingData,
  profile: MatchFilters
): number | null {
  const components = [
    budgetComponent(listing, profile),
    locationComponent(listing, profile),
    roomsComponent(listing, profile),
    sizeComponent(listing, profile),
    propertyTypeComponent(listing, profile)
  ].filter((c): c is ScoreComponent => c !== null);

  if (components.length < MIN_AVAILABLE_COMPONENTS) return null;

  const totalWeight = components.reduce((sum, c) => sum + c.weight, 0);
  const weighted = components.reduce((sum, c) => sum + c.weight * c.score, 0);
  const percent = (weighted / totalWeight) * 100;
  return Math.min(100, Math.max(0, Math.round(percent / 5) * 5));
}
